import React from 'react';

import Badge from 'atomic/badge';
import Link from 'atomic/link';
import ProductBoxPrice from 'modules/shared/components/product/components/product-box-price';
import Rating from 'atomic/rating';
import ProductListImpression from 'components/impressions/product-impression/list/product-list-impression';
import { ProductBoxNarrowProps } from 'modules/shared/components/product/product-box-narrow/product-box-narrow.props';
import classNames from 'classnames';
import AlertDiscountPriceInfo from 'modules/shared/components/alert-discount-price-info';
import ProductBoxName from 'modules/shared/components/product/components/product-box-name';
import { prepareOfferDataForDiscount } from 'modules/shared/helpers/prepare-offer-data-for-discount';

import './product-box-narrow.scss';

const ProductBox: React.FC<{ product: ProductBoxNarrowProps; mainImageLinkingToShop?: boolean; }> = ({ product, mainImageLinkingToShop }) => {
    const shouldImageRedirectToOffer = product.offers.count >= 1 && mainImageLinkingToShop && product.offers?.items[0]?.url;
    const {
        discount,
        minPrice30Days,
        price,
        offersCount,
        prevPrice,
        showDiscount,
        productHasNoOffers,
        dealerName
    } = prepareOfferDataForDiscount(
        product.minPrice,
        product.prevPrice,
        product.offers.items,
        product.offers.count
    );

    return (
        <ProductListImpression
            trackedData={{ ...product, category: { name: product.name }, price }}
            className="product-box-narrow-container"
        >
            { /* eslint-disable-next-line react/jsx-no-target-blank */}
            <a
                href={offersCount === 1 ? product.offers.items[0]?.url : product.url}
                rel="nofollow"
                target={offersCount === 1 ? '_blank' : void 0}
                className="product-box-narrow-link"
                aria-label={product.name}
            >
                {''}
            </a>
            <div className="product-box-narrow">
                {product.sponsored && (
                    <div className="product-box-narrow__sponsored">
                        <span>Oferta promowana</span>
                        {product.dsaurl && (
                            <a
                                className="product-box-narrow__sponsored__info"
                                href={product.dsaurl}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Info"
                            >
                                ⓘ
                            </a>
                        )}
                    </div>
                )}
                <div className="product-box-narrow__photo-box">
                    <div className="product-box-narrow__photo-box-image">
                        { shouldImageRedirectToOffer
                            ? (
                                <>
                                    <a
                                        href={product.offers?.items[0]?.url}
                                        rel="nofollow"
                                        className="product-box-narrow__photo-box-image__link"
                                    >
                                        <img
                                            src={product.photos.main?.thumb}
                                            alt={`${product.category.name} - ${product.name} - miniaturka - grafika 1`}
                                        />
                                    </a>
                                </>
                            )
                            : (
                                <>
                                    <img
                                        src={product.photos.main?.thumb}
                                        alt={`${product.category.name} - ${product.name} - miniaturka - grafika 1`}
                                    />
                                </>
                            )
                        }
                    </div>
                    <div className="product-box-narrow__photo-box-badges">
                        {showDiscount && (
                            <Badge color="red">
                                <span>{discount}%</span>
                            </Badge>
                        )}
                    </div>
                </div>
                <a
                    href={product.url}
                    data-ga="go-to-product-page"
                    rel={productHasNoOffers ? 'nofollow' : void 0}
                    className="product-box-narrow__title"
                >
                    <h2 title={product.name}>
                        {product.name}
                    </h2>
                </a>
                <Rating className="product-box-narrow__rating" opinions={product.opinions} />
                <div
                    className={classNames({
                        'product-box-narrow__price': true,
                        'product-box-narrow__price--one-offer': offersCount && offersCount === 1
                    })}
                >
                    <ProductBoxPrice
                        price={price}
                        prevPrice={prevPrice}
                        offersCount={offersCount}
                        discount={discount}
                    />
                    <ProductBoxName
                        dealerName={dealerName}
                        productHasNoOffers={productHasNoOffers}
                        className="product-box-narrow__price-producer-name"
                    />
                    {(minPrice30Days > 0 && offersCount > 0) && (
                        <div className="discount-info">
                            <AlertDiscountPriceInfo paddingTop={4} paddingBottom={2} minPrice30Days={minPrice30Days} />
                        </div>
                    )}
                </div>
                <div className="product-box-narrow__button-content">
                    <Link
                        href={product.offers?.items[0]?.url || product.url}
                        data-ga="go-to-shop"
                        data-cy="go-to-shop"
                        size="md"
                        color={product.offers.items[0]?.url ? 'primary' : 'transparent'}
                        type="button"
                        rel="nofollow"
                        className="product-box-narrow__button product-box-narrow__button--shop"
                    >
                        {product.offers?.items[0]?.url ? 'Idź do sklepu' : 'Zobacz produkt'}
                    </Link>
                    {offersCount && offersCount > 1
                        ? (
                            <Link
                                href={product.url}
                                data-ga="go-to-product-page go-to-product-page--d"
                                data-cy="go-to-product-page"
                                size="md"
                                color="transparent"
                                type="button"
                                rel="nofollow"
                                className="product-box-narrow__button"
                            >
                                Porównaj oferty ({offersCount})
                            </Link>
                        )
                        : (
                            <span className="producer-name-block">
                                {product.offers.items[0]?.dealer.name ? product.offers.items[0]?.dealer.name : ''}
                            </span>
                        )
                    }
                </div>
            </div>
        </ProductListImpression>
    );
};

export default ProductBox;
